"use client";

import React from "react";
import { Building2, FileCheck2, CalendarClock, Boxes, ClipboardCheck, House as Home, Users, Maximize2, TrendingUp, Wrench, ArrowRight, Clock } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
interface TimelineFactor {
  step: string;
  title: string;
  detail: string;
  leadTime: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
interface LifeTrigger {
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const TIMELINE_FACTORS: TimelineFactor[] = [{
  step: "01",
  title: "Design finalization & architectural plans",
  detail: "Full architectural schematics, structural engineering assessments, 3D renderings, and exact material selections before breaking ground.",
  leadTime: "4 - 8 Weeks",
  icon: Boxes
}, {
  step: "02",
  title: "City of Brookhaven & DeKalb County permitting",
  detail: "Zoning review, tree protection plans, building and trade permits through City of Brookhaven Community Development and DeKalb County.",
  leadTime: "4 - 10 Weeks",
  icon: Building2
}, {
  step: "03",
  title: "Material & mechanical system ordering",
  detail: "Advance procurement of custom cabinetry, luxury tile, glazing, and HVAC/plumbing equipment to safeguard against supply lead times.",
  leadTime: "6 - 12 Weeks",
  icon: FileCheck2
}, {
  step: "04",
  title: "Multi-room trade scheduling & mobilization",
  detail: "Sequential coordination of framing, rough-ins, drywallers, and trim carpenters to keep dust barriers intact and subcontractors on schedule.",
  leadTime: "Concurrent",
  icon: CalendarClock
}, {
  step: "05",
  title: "Municipal inspections & final sign-offs",
  detail: "Milestone building, mechanical, electrical, and plumbing inspections, followed by final inspection and Certificate of Occupancy.",
  leadTime: "At Project Milestones",
  icon: ClipboardCheck
}];
const LIFE_TRIGGERS: LifeTrigger[] = [{
  title: "Purchased an older Brookhaven home",
  description: "Preserve neighborhood curb appeal and historic character while updating floor plans, wiring, plumbing, and insulation for today's living.",
  icon: Home
}, {
  title: "Need for an open, connected layout",
  description: "Eliminate compartmentalized mid-century rooms to combine kitchen, dining, and living zones for modern hosting and daily flow.",
  icon: Maximize2
}, {
  title: "Growing family needing functional space",
  description: "Add or reconfigure bedrooms, build dedicated mudrooms, expand bathrooms, and eliminate daily bottlenecks as your household evolves.",
  icon: Users
}, {
  title: "Outdated, failing mechanical systems",
  description: "Replace aging galvanized lines, undersized electrical panels, inefficient HVAC units, and uninsulated walls before interior finishes fail.",
  icon: Wrench
}, {
  title: "Preparing to sell or maximize long-term equity",
  description: "Align your property's finishes, layout, and energy performance with top-tier Brookhaven market expectations for maximum valuation.",
  icon: TrendingUp
}];
export default function TimelineTriggers() {
  return <section id="remodeltimelineandtriggers" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section 1: Timeline Factors */}
        <div className="space-y-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end">
            <div className="lg:col-span-7 space-y-4">
              <Badge variant="outline" className="text-secondary border-border font-medium">
                Planning & Municipal Execution
              </Badge>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-normal text-foreground tracking-tight leading-tight">
                Whole-home remodel timeline factors in Brookhaven
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed max-w-2xl">
                A predictable renovation requires navigating municipal permits, structural blueprints, and material schedules before demolition ever begins. Here is what dictates your project duration.
              </p>
            </div>
            <div className="lg:col-span-5 flex lg:justify-end">
              <div className="p-4 rounded-lg bg-muted/60 border border-border flex items-center gap-3 text-sm text-foreground">
                <Clock className="w-5 h-5 text-primary shrink-0" />
                <span>Typical whole-home duration: <strong>5 to 9 months</strong> active construction</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Timeline Steps List */}
            <div className="lg:col-span-7 space-y-4">
              {TIMELINE_FACTORS.map((factor, index) => {
              const IconComponent = factor.icon;
              return <Card key={factor.step} data-index={index} className="border-border bg-card shadow-xs hover:border-primary/50 transition-colors">
                    <CardHeader className="flex flex-row items-start justify-between gap-4 pb-2">
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-xs font-semibold text-primary px-2 py-1 bg-primary/10 rounded-sm">
                          {factor.step}
                        </span>
                        <CardTitle className="text-lg font-serif font-medium text-foreground">
                          {factor.title}
                        </CardTitle>
                      </div>
                      <Badge variant="secondary" className="shrink-0 text-xs font-normal">
                        {factor.leadTime}
                      </Badge>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-sm text-muted-foreground leading-relaxed pl-9">
                        {factor.detail}
                      </p>
                    </CardContent>
                  </Card>;
            })}
            </div>

            {/* Supporting Visual & Brookhaven Context */}
            <div className="lg:col-span-5 space-y-6">
              <div className="rounded-xl overflow-hidden border border-border shadow-md bg-card">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_architectural_plans_remodel.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_architectural_plans_remodel.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_architectural_plans_remodel.webp-wvc-sizes" alt="Architectural plans and structural permits for a Brookhaven residential renovation" loading="lazy" decoding="async" className="w-full h-72 object-cover" />
                <div className="p-6 space-y-3">
                  <h3 className="font-serif text-xl font-medium text-foreground">
                    Local permitting experience
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Brookhaven Elevation Renovations handles submission filings directly with the City of Brookhaven Community Development department and DeKalb County reviewers, eliminating inspection holdups and re-submission delays.
                  </p>
                  <div className="pt-2">
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/ContactRequestAnEstimatePage">
                        Schedule an on-site feasibility review
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Separator className="my-16 lg:my-24" />

        {/* Section 2: Is It Time for a Whole-Home Remodel? */}
        <div className="space-y-12">
          <div className="max-w-3xl space-y-4">
            <Badge variant="outline" className="text-secondary border-border font-medium">
              Home Life Transitions
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-normal text-foreground tracking-tight leading-tight">
              Is it time for a whole-home remodel?
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
              Renovating room-by-room often leads to mismatched flooring, fragmented plumbing, and years of ongoing disruption. When your lifestyle outpaces your floor plan, an integrated whole-home project creates a cohesive residence.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Triggers List */}
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {LIFE_TRIGGERS.map((trigger, i) => {
              const TriggerIcon = trigger.icon;
              return <Card key={trigger.title} data-index={i} className={`border-border bg-card shadow-xs transition-shadow hover:shadow-md ${i === 0 ? "sm:col-span-2" : ""}`}>
                    <CardHeader className="flex flex-row items-center gap-3 pb-2">
                      <div className="p-2 rounded-md bg-primary/10 text-primary shrink-0">
                        <TriggerIcon className="w-5 h-5" />
                      </div>
                      <CardTitle className="text-base font-serif font-medium text-foreground">
                        {trigger.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {trigger.description}
                      </p>
                    </CardContent>
                  </Card>;
            })}
            </div>

            {/* Photography Card */}
            <div className="lg:col-span-5 space-y-4">
              <div className="relative rounded-xl overflow-hidden border border-border shadow-lg">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/whole_home_open_concept_remodel_brookhaven.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/whole_home_open_concept_remodel_brookhaven.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/whole_home_open_concept_remodel_brookhaven.png-wvc-sizes" alt="Completed whole-home open layout remodel with custom kitchen and hardwood floors in Brookhaven home" loading="lazy" decoding="async" className="w-full h-96 object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 via-transparent to-transparent flex flex-col justify-end p-6">
                  <span className="text-xs font-mono tracking-wide uppercase text-primary font-medium">
                    Integrated Design-Build
                  </span>
                  <p className="text-secondary-foreground font-serif text-lg font-medium mt-1">
                    Continuous flooring, unified lighting, and synchronized mechanical upgrades.
                  </p>
                </div>
              </div>

              <div className="p-6 rounded-xl bg-card border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h3 className="font-serif text-base font-medium text-foreground">
                    Discuss your home’s next chapter
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Consultations are complimentary and detailed.
                  </p>
                </div>
                <Button size="default" className="shrink-0" asChild>
                  <Link to="/ContactRequestAnEstimatePage">
                    Request an Estimate
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}